import { apiClient } from '../utils/apiClient'

export interface ChatRequest {
  message: string
  provider?: {
    provider: string
    model: string
  }
}

export interface ChatResponse {
  content: string
  provider: {
    provider: string
    model: string
  }
}

export interface Provider {
  provider: string
  model: string
  available: boolean
  status?: string
}

export class ChatService {
  async sendMessage(message: string, provider?: { provider: string; model: string }, debugMode = false): Promise<ChatResponse> {
    try {
      const requestBody: any = {
        message: message,
        debugMode: debugMode
      }

      // Add provider parameter if specified
      if (provider) {
        requestBody.provider = provider.provider
        // For OpenRouter, also specify the model to use
        if (provider.provider === 'openrouter') {
          requestBody.model = provider.model
        }
      }

      const data = await apiClient.post<any>('/api/chat', requestBody)

      // In debug mode, show enhanced debug information
      if (debugMode) {
        const responseProvider = data.metadata?.provider || provider || { provider: 'anthropic', model: 'claude-sonnet-4-6' }
        const actualModel = responseProvider.actualModel || responseProvider.model || (provider?.model)

        const debugContent = [
          `**REQUEST ID:** ${data.metadata?.requestId || 'N/A'}`,
          `**PROVIDER:** ${responseProvider.name || responseProvider.provider || 'N/A'} (${actualModel || 'N/A'})`,
          `**RESPONSE TIME:** ${data.metadata?.responseTime || 'N/A'}ms`,
          `**TOKENS:** Input: ${data.usage?.inputTokens || 'N/A'}, Output: ${data.usage?.outputTokens || 'N/A'}`,
          `**CEREBRAS BOOST:** ${data.metadata?.cerebrasBoosted ? 'Yes' : 'No'}`,
          ``,
          `**SYSTEM PROMPT:**`,
          `\`\`\``,
          data.metadata?.systemPrompt || 'Not available',
          `\`\`\``,
          ``,
          `**RESPONSE:**`,
          `\`\`\``,
          data.content || 'No content',
          `\`\`\``,
          ``,
          `**RAW RESPONSE:**`,
          `\`\`\`json`,
          JSON.stringify(data, null, 2),
          `\`\`\``
        ].join('\n')

        return {
          content: debugContent,
          provider: {
            provider: responseProvider.name || responseProvider.provider,
            model: actualModel
          }
        }
      }

      // Handle chat response format
      const content = data.content || 'No response received'
      const responseProvider = data.metadata?.provider || provider || { provider: 'anthropic', model: 'claude-sonnet-4-6' }

      // Use the actual selected model if available, otherwise fallback to provider model
      const actualModel = responseProvider.actualModel || responseProvider.model || (provider?.model)

      return {
        content: content,
        provider: {
          provider: responseProvider.name || responseProvider.provider,
          model: actualModel
        }
      }
    } catch (error) {
      console.error('Error sending chat message:', error)
      throw error
    }
  }

  async getProviders(): Promise<Provider[]> {
    try {
      const data = await apiClient.get<any>('/api/providers')
      console.log('Providers API response:', data)

      // NEW ARCHITECTURE: Backend now provides complete model list
      if (data.models && Array.isArray(data.models)) {
        console.log(`✅ Loaded ${data.models.length} models from backend providers`)
        return data.models
      }

      // Fallback if no models in response
      console.warn('No models array in API response, using defaults')
      return this.getDefaultProviders()
    } catch (error) {
      console.error('Error fetching providers:', error)
      return this.getDefaultProviders()
    }
  }

  private getDefaultProviders(): Provider[] {
    // Minimal fallback if backend API is unavailable
    // These should only be used in emergency scenarios
    console.warn('⚠️ Using minimal fallback providers - backend API unavailable')
    return [
      {
        provider: 'anthropic',
        model: 'claude-sonnet-4-6',
        available: true,
        status: 'Claude 4.6 Sonnet (Fallback)'
      },
      {
        provider: 'openrouter',
        model: 'qwen/qwen3-235b-a22b-2507',
        available: true,
        status: 'Cerebras Ultra-Fast (Fallback)'
      }
    ]
  }

  async switchProvider(provider: string, model?: string): Promise<void> {
    try {
      await apiClient.post('/api/providers/switch', { provider, model })
    } catch (error) {
      console.error('Error switching provider:', error)
      throw error
    }
  }

  async getProviderStatus(): Promise<{ currentProvider: string; currentModel: string }> {
    try {
      const data = await apiClient.get<any>('/api/providers/status')
      console.log('Provider status response:', data)

      // Handle the actual API response format
      const defaultProvider = data.defaultProvider || 'anthropic'
      let currentModel = 'claude-sonnet-4-6'

      if (data.providers && data.providers[defaultProvider]) {
        currentModel = data.providers[defaultProvider].info?.model || currentModel
      }

      return {
        currentProvider: defaultProvider,
        currentModel: currentModel
      }
    } catch (error) {
      console.error('Error getting provider status:', error)
      // Return default if fetch fails
      return {
        currentProvider: 'anthropic',
        currentModel: 'claude-sonnet-4-6'
      }
    }
  }
}

export const chatService = new ChatService()
